import { Dialog, Disclosure, Menu, Transition } from "@headlessui/react";
import { ArrowRightOnRectangleIcon, Bars3Icon, CogIcon, UserIcon, XMarkIcon } from "@heroicons/react/24/outline";
import { ArrowRightIcon } from "@heroicons/react/24/solid";
import { ArrowUpRightIcon } from "@primer/octicons-react";
import classNames from "classnames";
import { QueryConstraint, where } from "firebase/firestore";
import mobile from "is-mobile";
import Head from "next/head";
import Link from "next/link";
import { useRouter } from "next/router";
import { Fragment, useCallback, useMemo, useRef, useState } from "react";
import * as awarenessProtocol from "y-protocols/awareness";
import { POLAR_PRODUCT_IDS, WEB_URL, WWW_URL } from "../../../.env.current";
import { YFSClient } from "../../../libEdit/yfs/YFSClient";
import { useBackend } from "../../util/backend";
import {
  DEPLOY_TYPE_AVAILABLE_OPTIONS,
  EDITOR_TYPE_AVAILABLE_OPTIONS,
  REPO_TYPE_AVAILABLE_OPTIONS,
  RESOURCE_VISIBILITY_AVAILABLE_OPTIONS,
} from "../../util/consts";
import { useFirebase } from "../../util/firebase";
import { getCurrentUrl } from "../../util/router";
import { Owner } from "../../util/types";
import { getDeployBaseHref, getEditorFileHref, getRepoObjectHref } from "../../util/url";
import { useGlobalState } from "../../util/useGlobalState";
import DateDisplay from "../common/DateDisplay";
import { ForwardRefNextLink } from "../common/Link";
import { Intent, useNotification } from "../common/Notification";
import Spinner from "../common/Spinner";
import { NavbarTitle } from "../common/Title";
import UserAvatar from "../common/UserAvatar";
import Presence from "../editor/Presence";
import SimpleCombobox from "../input/SimpleCombobox";

const UNCLAIMED_RESOURCE_EXPIRES_AFTER_MS = 6 * 60 * 60 * 1000;

export default function LayoutApp({
  documentTitle,
  title,
  yfsClient,
  awareness,
  resourceOwner,
  resource,
  refName,
  filePath,
  children,
}: {
  documentTitle?: React.ReactNode;
  title: React.ReactNode;
  yfsClient: YFSClient | undefined;
  awareness: awarenessProtocol.Awareness | undefined;
  resourceOwner?: Owner | undefined;
  resource?: any | undefined;
  refName?: string | undefined;
  filePath?: string | undefined;
  children?: React.ReactNode;
}) {
  const router = useRouter();
  const firebase = useFirebase();
  const [globalState, _setGlobalState] = useGlobalState();
  const { addNotification } = useNotification();
  const isMobile = useMemo(() => mobile(), []);

  const { data: dataProfile, loading: _loadingProfile } = firebase.useFirestoreWatchDoc(
    "UserProfile",
    firebase.authUser?.uid
  );
  const dataFullNameValue = useMemo(() => dataProfile?.fullName || "", [dataProfile]);

  const [usernameQueryConstraints, usernameQueryOptions] = useMemo(() => {
    const c: QueryConstraint[] = [];
    c.push(where("userId", "==", firebase.authUser?.uid));
    const o = { enabled: Boolean(firebase.authUser?.uid), shouldSetDocs: true };
    return [c, o];
  }, [firebase.authUser?.uid]);

  const { data: dataUsername } = firebase.useFirestoreWatchQuery(
    "Username",
    usernameQueryConstraints,
    usernameQueryOptions
  );
  const currentUsername = useMemo(() => dataUsername?.[0]?._id, [dataUsername]);

  const NAVIGATION_MAIN = useMemo(
    () => [
      ...(firebase?.authUser
        ? [
            {
              name: "Dashboard",
              url: new URL(`${WEB_URL}/dashboard`),
              isActive: () => router.pathname === "/dashboard",
              isExternal: false,
            },
          ]
        : []),
      {
        name: "Start",
        url: new URL(WWW_URL),
        isActive: () => false,
        isExternal: false,
      },
      {
        name: "Pricing",
        url: new URL(`${WWW_URL}/pricing`),
        isActive: () => false,
        isExternal: false,
      },
    ],
    [firebase.authUser, router]
  );

  const NAVIGATION_USER = useMemo(
    () => [
      {
        name: "settings",
        url: new URL(`${WEB_URL}/settings`),
        isActive: () => {
          const currentUrl = getCurrentUrl();
          return currentUrl?.pathname === "/settings" || currentUrl?.pathname?.startsWith("/settings/");
        },
        children: (
          <>
            <CogIcon className="inline mr-2 mb-1 h-5 w-5" aria-hidden="true" />
            Settings
          </>
        ),
      },
      {
        name: "profile",
        url: !currentUsername ? undefined : new URL(`${WEB_URL}/@${currentUsername}`),
        isActive: () => {
          const currentUrl = getCurrentUrl();
          return Boolean(currentUsername) && currentUrl?.pathname === `/@${currentUsername}`;
        },
        children: (
          <>
            <UserIcon className="inline mr-2 mb-1 h-5 w-5" aria-hidden="true" />
            Profile
          </>
        ),
      },
      {
        name: "sign-out",
        onClick: () => {
          if (!confirm("Sign out?")) return;
          firebase.auth?.signOut();
        },
        isActive: () => false as boolean,
        children: (
          <>
            <ArrowRightOnRectangleIcon className="inline mr-2 mb-1 h-5 w-5" aria-hidden="true" />
            Sign out
          </>
        ),
      },
    ],
    [firebase, currentUsername]
  );

  //--- USER PLAN ACTIVE ------------------------------------------------------
  const { data: dataPlanActive, loading: loadingPlanActive } = firebase.useFirestoreWatchDoc(
    "UserPlanActive",
    firebase.authUser?.uid
  );

  const planIdActive = useMemo(() => {
    const polarPlanIdActive = dataPlanActive?.polarPlanId;
    const planActiveEndsAt = dataPlanActive?.endsAt;

    let _planIdActive = "plan-free";
    for (const [_planId, _polarPlanId] of Object.entries(POLAR_PRODUCT_IDS)) {
      if (_polarPlanId === polarPlanIdActive) {
        _planIdActive = _planId;
        break;
      }
    }
    if (_planIdActive !== "plan-free" && (!planActiveEndsAt || new Date() < planActiveEndsAt?.toDate?.())) {
      return _planIdActive;
    }
    return "plan-free";
  }, [dataPlanActive]);

  //--- CLAIM UNCLAIMED RESOURCES ---------------------------------------------
  const [claimDialog, setClaimDialog] = useState(null as null | { claiming?: boolean });
  const claimRepoNameRef = useRef(null);
  const [claimState, setClaimState] = useState(() => {
    const randomSuffix = Math.floor(Math.random() * 0xffffff).toString(16).padStart(6, "0"); // prettier-ignore
    return { visibility: "private", repoName: `repo-${randomSuffix}`, editorName: `editor-${randomSuffix}` };
  });

  const { backendPostClaimUnclaimed } = useBackend();
  const onClaimUnclaimed = useCallback(
    (resourceId: string, repoName: string, editorName: string, visibility: string) => {
      if (!backendPostClaimUnclaimed) return;
      setClaimDialog({ claiming: true });
      backendPostClaimUnclaimed(resourceId, repoName, editorName, visibility)
        .then(() => {
          setClaimDialog(null);
          const currentOwner: Owner = currentUsername
            ? { kind: "userByUsername", username: currentUsername }
            : { kind: "userCurrent" };

          let redirectHref: string | undefined;
          if (REPO_TYPE_AVAILABLE_OPTIONS.find((o) => o.name === resource.type)) {
            redirectHref = getRepoObjectHref(currentOwner, repoName, refName, filePath);
          }
          if (EDITOR_TYPE_AVAILABLE_OPTIONS.find((o) => o.name === resource.type)) {
            redirectHref = getEditorFileHref(currentOwner, editorName, filePath);
          }
          if (DEPLOY_TYPE_AVAILABLE_OPTIONS.find((o) => o.name === resource.type)) {
            redirectHref = getDeployBaseHref(currentOwner, resourceId);
          }
          if (redirectHref) {
            router.replace(redirectHref, undefined, { shallow: true });
          }
        })
        .catch((err) => {
          addNotification({
            intent: Intent.ERROR,
            title: err?.getFriendlyDescription?.(),
            message: err?.message,
            hideAfterMs: 4000,
          });
        })
        .finally(() => {
          setClaimDialog((c: any) => (!c ? c : { claiming: false }));
        });
    },
    [router, currentUsername, resource, refName, filePath, backendPostClaimUnclaimed, setClaimDialog, addNotification]
  );

  return (
    <>
      {resourceOwner?.kind === "unclaimed" && (
        <div className="flex justify-center items-center gap-x-6 bg-stone-800 py-2.5 sm:py-3.5 pl-2.5 pr-2">
          <div className="min-h-9 sm:min-h-7 w-full sm:w-auto flex justify-between items-center gap-x-2.5 sm:gap-x-4">
            <div className="text-xs sm:text-sm text-white">
              <strong className="inline mr-2 font-semibold">This project doesn&apos;t belong to anyone</strong>
              <svg viewBox="0 0 2 2" aria-hidden="true" className="inline mr-2 size-0.5 fill-current">
                <circle r={1} cx={1} cy={1} />
              </svg>
              <span className="inline">
                {firebase.authUser ? (
                  "Claim"
                ) : (
                  <>
                    <Link href={`/signup?${new URLSearchParams({ pathFrom: router.asPath })}`} className="">
                      Sign up
                    </Link>{" "}
                    and claim
                  </>
                )}{" "}
                it or it will expire{" "}
                <DateDisplay
                  date={
                    resource?.createdAt?.toDate?.() &&
                    new Date(resource?.createdAt?.toDate?.()?.getTime() + UNCLAIMED_RESOURCE_EXPIRES_AFTER_MS)
                  }
                  className="inline"
                  emptyText="sometime"
                />
              </span>
            </div>
            {firebase.authUser && resource && (
              <button
                type="button"
                className="h-9 sm:h-auto rounded-sm bg-white px-3.5 py-1 text-sm font-semibold text-gray-900 shadow-xs ring-1 ring-gray-300 ring-inset hover:bg-gray-50"
                onClick={() => setClaimDialog({})}
              >
                Claim
              </button>
            )}
          </div>
        </div>
      )}
      {documentTitle && (
        <Head key="title">
          <title>{documentTitle}</title>
        </Head>
      )}
      <div className="min-h-screen bg-white flex flex-col">
        <Disclosure as="nav" className="bg-gray-200/70 border-b border-gray-400/50">
          {({ open }) => (
            <>
              <div className="mx-auto max-w-7xl pl-3 pr-2 sm:pl-6 sm:pr-6 lg:pl-8 lg:pr-8">
                <div className="flex h-16 items-center justify-between">
                  <div className="flex items-center">
                    <div className="shrink-0">
                      <NavbarTitle />
                    </div>
                    <div className="hidden md:block">
                      <div className="ml-12 lg:ml-14 mt-0.5 flex items-baseline space-x-4">
                        {NAVIGATION_MAIN.map((item) => {
                          const isCurrent = item.isActive();
                          return (
                            <Link
                              key={item.name}
                              href={item.url.href}
                              className={classNames(
                                isCurrent
                                  ? "bg-slate-600/80 text-white"
                                  : "hover:bg-slate-300 text-gray-800 hover:text-gray-900",
                                "px-2.5 py-[7px] rounded-md text-sm font-medium",
                                "flex items-center gap-x-1"
                              )}
                              aria-current={isCurrent ? "page" : undefined}
                            >
                              {item.name}
                              {item.isExternal && (
                                <ArrowUpRightIcon className="inline h-[13px] w-auto" aria-hidden="true" />
                              )}
                            </Link>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                  <div className="flex-1 flex justify-end">
                    {yfsClient && awareness && (
                      <div className="-mr-0.5">
                        <Presence yfsClient={yfsClient} awareness={awareness} />
                      </div>
                    )}
                  </div>
                  <div className="hidden md:block">
                    {firebase?.authLoading ? (
                      <span className="ml-3 md:ml-5 flex items-center space-x-[5px] text-gray-800 hover:text-gray-900 opacity-50">
                        <div>Sign up</div>
                        <ArrowRightIcon className="h-3.5 w-auto" aria-hidden="true" />
                      </span>
                    ) : !firebase?.authUser ? (
                      <Link
                        href={`/signup?${new URLSearchParams({ pathFrom: router.asPath })}`}
                        className="ml-3 md:ml-5 flex items-center space-x-[5px] text-gray-800 hover:text-gray-900"
                      >
                        <div>Sign up</div>
                        <ArrowRightIcon className="h-3.5 w-auto" aria-hidden="true" />
                      </Link>
                    ) : (
                      <div className="ml-3 md:ml-5 flex items-center">
                        {!loadingPlanActive && planIdActive === "plan-free" && (
                          <Link
                            className="mr-[14px] text-sm font-bold text-indigo-700"
                            href={{
                              pathname: "/settings/account",
                              query: { ...router.query, plan: router.query["plan"] ?? "plan-builder" },
                            }}
                          >
                            Upgrade
                          </Link>
                        )}
                        <Menu as="div" className="relative">
                          <div className="">
                            <Menu.Button className="flex max-w-xs items-center rounded-full text-sm text-white">
                              <span className="sr-only">Open user menu</span>
                              <UserAvatar
                                userId={firebase.authUser?.uid}
                                className="h-10 w-10 p-0.5 ring-1 ring-black"
                                timestamp={globalState?.avatarChanged}
                              />
                            </Menu.Button>
                          </div>
                          <Transition
                            as={Fragment}
                            enter="transition ease-out duration-100"
                            enterFrom="transform opacity-0 scale-95"
                            enterTo="transform opacity-100 scale-100"
                            leave="transition ease-in duration-75"
                            leaveFrom="transform opacity-100 scale-100"
                            leaveTo="transform opacity-0 scale-95"
                          >
                            <Menu.Items className="absolute right-0 z-10 mt-2 w-48 origin-top-right rounded-md bg-white py-1 shadow-lg ring-1 ring-black/10 focus:outline-hidden">
                              {NAVIGATION_USER.map(({ name, url, onClick, isActive, ...rest }: any) => {
                                const isCurrent = isActive();
                                return (
                                  <Fragment key={name}>
                                    {url != undefined ? (
                                      <Menu.Item>
                                        {({ active, close: _close }) => (
                                          <ForwardRefNextLink
                                            href={url.href}
                                            className={classNames(
                                              "w-full block text-left px-4 py-2 text-sm text-gray-800",
                                              {
                                                "bg-gray-200/70": active,
                                                underline: isCurrent,
                                              }
                                            )}
                                            {...rest}
                                          />
                                        )}
                                      </Menu.Item>
                                    ) : onClick != undefined ? (
                                      <Menu.Item>
                                        {({ active, close: _close }) => (
                                          <button
                                            onClick={onClick}
                                            className={classNames(
                                              "w-full block text-left px-4 py-2 text-sm text-gray-800",
                                              {
                                                "bg-gray-200/70": active,
                                                underline: isCurrent,
                                              }
                                            )}
                                            {...rest}
                                          />
                                        )}
                                      </Menu.Item>
                                    ) : (
                                      <div
                                        onClick={onClick}
                                        className={classNames(
                                          "w-full block text-left px-4 py-2 text-sm text-gray-800",
                                          "opacity-50"
                                        )}
                                        {...rest}
                                      />
                                    )}
                                  </Fragment>
                                );
                              })}
                            </Menu.Items>
                          </Transition>
                        </Menu>
                      </div>
                    )}
                  </div>
                  <div className="ml-4 flex md:hidden">
                    {/* Mobile menu button */}
                    {firebase?.authLoading ? (
                      <div className="animate-pulse p-1.5">
                        <Bars3Icon className="block h-6 w-6 text-gray-400" aria-hidden="true" />
                      </div>
                    ) : !firebase?.authUser ? (
                      <Link
                        href={`/signup?${new URLSearchParams({ pathFrom: router.asPath })}`}
                        className="flex items-center space-x-1 text-gray-800 hover:text-gray-900 mr-2"
                      >
                        <div>Sign up</div>
                        <ArrowRightIcon className="ml-0.5 mt-px h-3.5 w-auto" aria-hidden="true" />
                      </Link>
                    ) : (
                      <Disclosure.Button className="inline-flex items-center justify-center rounded-md bg-gray-400 p-1.5 text-white hover:bg-gray-500 hover:text-gray-100 focus:outline-hidden focus:ring-2 focus:ring-gray-800">
                        <span className="sr-only">Open main menu</span>
                        {open ? (
                          <XMarkIcon className="block h-6 w-6" aria-hidden="true" />
                        ) : (
                          <Bars3Icon className="block h-6 w-6" aria-hidden="true" />
                        )}
                      </Disclosure.Button>
                    )}
                  </div>
                </div>
              </div>

              <Disclosure.Panel className="md:hidden">
                {({ close }) => (
                  <>
                    <div className="flex gap-x-1 px-2 pt-2 pb-3 sm:px-3">
                      {NAVIGATION_MAIN.map((item) => {
                        const isCurrent = item.isActive();
                        return (
                          <Link
                            key={item.name}
                            href={item.url.href}
                            onClick={() => close()}
                            className={classNames(
                              isCurrent
                                ? "bg-slate-600/80 text-white"
                                : "hover:bg-gray-700 text-gray-800 hover:text-white",
                              "flex-1 px-2 py-[7px] rounded-md text-sm font-medium",
                              "flex justify-center items-center gap-x-[3px]"
                            )}
                            aria-current={isCurrent ? "page" : undefined}
                          >
                            {item.name}
                            {item.isExternal && <ArrowUpRightIcon className="inline h-3 w-auto" aria-hidden="true" />}
                          </Link>
                        );
                      })}
                    </div>
                    <div className="border-t border-gray-700 pt-4 pb-3">
                      <div className="flex justify-between items-center">
                        <Link onClick={() => close()} href="/settings" className="flex-1 flex items-center px-5">
                          <div className="shrink-0">
                            <UserAvatar
                              userId={firebase.authUser?.uid}
                              className="h-10 w-10 p-0.5 ring-1 ring-black"
                              timestamp={globalState?.avatarChanged}
                            />
                          </div>
                          <div className="ml-3">
                            <div className="text-base font-medium text-black">{dataFullNameValue}</div>
                            <div className="text-sm font-medium text-gray-600">{firebase.authUser?.email || ""}</div>
                          </div>
                        </Link>

                        {!loadingPlanActive && planIdActive === "plan-free" && (
                          <Link
                            onClick={() => close()}
                            className="flex-1 py-2.5 mr-4.5 text-base font-bold text-indigo-700 text-right"
                            href={{
                              pathname: "/settings/account",
                              query: { ...router.query, plan: router.query["plan"] ?? "plan-builder" },
                            }}
                          >
                            Upgrade
                          </Link>
                        )}
                      </div>
                      <div className="mt-3 space-y-1 px-2">
                        {NAVIGATION_USER.map(({ name, url, onClick, isActive, ...rest }: any) => {
                          const isCurrent = isActive();
                          return (
                            <Fragment key={name}>
                              {url != undefined ? (
                                <ForwardRefNextLink
                                  href={url.href}
                                  onClick={() => close()}
                                  className={classNames(
                                    isCurrent
                                      ? "bg-slate-600/80 text-white"
                                      : "hover:bg-gray-700 text-gray-800 hover:text-white",
                                    "block px-3 py-2 rounded-md text-base font-medium",
                                    "w-full text-left"
                                  )}
                                  {...rest}
                                />
                              ) : onClick != undefined ? (
                                <button
                                  onClick={() => {
                                    onClick();
                                    close();
                                  }}
                                  className={classNames(
                                    isCurrent
                                      ? "bg-slate-600/80 text-white"
                                      : "hover:bg-gray-700 text-gray-800 hover:text-white",
                                    "block px-3 py-2 rounded-md text-base font-medium",
                                    "w-full text-left"
                                  )}
                                  {...rest}
                                />
                              ) : (
                                <div
                                  className={classNames(
                                    "block rounded-md px-3 py-2 text-base font-medium hover:bg-gray-400 text-gray-800 hover:text-gray-900",
                                    "opacity-50"
                                  )}
                                  {...rest}
                                />
                              )}
                            </Fragment>
                          );
                        })}
                      </div>
                    </div>
                  </>
                )}
              </Disclosure.Panel>
            </>
          )}
        </Disclosure>

        <header className="border-b border-gray-400/50">
          <div className="w-full h-[56px] mx-auto max-w-7xl pl-3 pr-2 sm:pl-6 sm:pr-6 lg:pl-8 lg:pr-8">
            <h1 className="w-full h-full text-lg font-semibold leading-6 text-gray-900 flex items-center">{title}</h1>
          </div>
        </header>

        <main className="grow">
          <div className="mx-auto max-w-7xl py-4 sm:py-6 px-2 sm:px-6 lg:px-8">{children}</div>
        </main>

        <footer className="">
          <div className="mx-auto max-w-7xl px-6 pt-10 pb-4 md:flex md:items-center md:justify-center lg:px-8">
            <div className="mt-8 md:order-1 md:mt-0">
              {/* <p className="text-center text-xs leading-5 text-gray-500">&copy; 2025 Gitlip.</p> */}
            </div>
          </div>
        </footer>
      </div>

      <style jsx global>{`
        html {
          overflow-y: scroll;
        }
      `}</style>

      <Transition.Root show={Boolean(claimDialog)} as={Fragment}>
        <Dialog
          as="div"
          className="relative z-10"
          initialFocus={claimRepoNameRef}
          onClose={(v: boolean) => setClaimDialog((c) => (v ? c : null))}
        >
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-gray-500/75 transition-opacity" />
          </Transition.Child>

          <div className="fixed inset-0 z-10 overflow-y-auto">
            <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                enterTo="opacity-100 translate-y-0 sm:scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              >
                <Dialog.Panel className="relative transform rounded-lg bg-white px-4 pt-5 pb-4 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-md sm:p-6">
                  <form
                    onSubmit={(e) => {
                      e.preventDefault();

                      const _resourceIdWithSuffix = resource._id;
                      if (!_resourceIdWithSuffix) return;

                      let _resourceId: string | undefined;
                      if (_resourceIdWithSuffix.startsWith("repo-")) {
                        _resourceId = _resourceIdWithSuffix.slice(-_resourceIdWithSuffix.length + "repo-".length);
                      } else if (_resourceIdWithSuffix.startsWith("editor-")) {
                        _resourceId = _resourceIdWithSuffix.slice(-_resourceIdWithSuffix.length + "editor-".length);
                      } else {
                        _resourceId = _resourceIdWithSuffix;
                      }
                      if (!_resourceId) return;

                      onClaimUnclaimed(_resourceId, claimState.repoName, claimState.editorName, claimState.visibility);
                    }}
                  >
                    <div>
                      <div className="mt-3 sm:mt-5">
                        <Dialog.Title as="h3" className="text-center text-lg font-medium leading-6 text-gray-900">
                          <div className="w-full text-lg text-gray-900 flex justify-center items-center">
                            Claim project
                          </div>
                        </Dialog.Title>

                        <div className="mt-3">
                          <p className="text-sm text-gray-500">
                            This will transfer the project and all associated resources to your account
                          </p>
                        </div>

                        <div className="mt-5 py-5 space-y-4">
                          {/* --- REPO NAME ------------------------------------------------ */}
                          <div>
                            <label htmlFor="repo-name" className="block text-sm font-medium text-gray-700">
                              New name for repo <span className="text-gray-700/60">(spaces not allowed)</span>
                            </label>
                            <div className="mt-1">
                              <input
                                ref={claimRepoNameRef}
                                id="repo-name"
                                name="repo-name"
                                type="text"
                                required
                                placeholder="my-awesome-repo"
                                value={claimState.repoName}
                                onChange={(e) => {
                                  setClaimState((c) => ({
                                    ...c,
                                    repoName: e.target.value || "",
                                  }));
                                }}
                                className="block w-full appearance-none rounded-md border border-gray-400 px-3 py-[7px] placeholder-gray-400 shadow-xs focus:border-indigo-500 focus:outline-hidden focus:ring-indigo-500 sm:text-sm"
                              />
                            </div>
                          </div>

                          {/* --- EDITOR NAME ---------------------------------------------- */}
                          <div>
                            <label htmlFor="editor-name" className="block text-sm font-medium text-gray-700">
                              New name for editor <span className="text-gray-700/60">(spaces not allowed)</span>
                            </label>
                            <div className="mt-1">
                              <input
                                id="editor-name"
                                name="editor-name"
                                type="text"
                                required
                                placeholder="my-awesome-editor"
                                value={claimState.editorName}
                                onChange={(e) => {
                                  setClaimState((c) => ({
                                    ...c,
                                    editorName: e.target.value || "",
                                  }));
                                }}
                                className="block w-full appearance-none rounded-md border border-gray-400 px-3 py-[7px] placeholder-gray-400 shadow-xs focus:border-indigo-500 focus:outline-hidden focus:ring-indigo-500 sm:text-sm"
                              />
                            </div>
                          </div>

                          {/* --- EDITOR VISIBILITY ---------------------------------------- */}
                          <SimpleCombobox
                            selectedOptionName={claimState.visibility}
                            availableOptions={RESOURCE_VISIBILITY_AVAILABLE_OPTIONS}
                            onChange={(v) => {
                              setClaimState((c) => ({
                                ...c,
                                visibility: v ?? "private",
                              }));
                            }}
                            labelProps={{
                              htmlFor: "resource-visibility",
                              children: "New visibility",
                            }}
                            inputProps={{ readOnly: isMobile }}
                          />
                        </div>
                      </div>
                    </div>
                    <div className="mt-5 sm:mt-6 sm:grid sm:grid-flow-row-dense sm:grid-cols-2 sm:gap-3">
                      <button
                        type="submit"
                        className={classNames(
                          { "bg-indigo-900": claimDialog?.claiming },
                          "inline-flex w-full justify-center items-center rounded-md border border-transparent bg-indigo-600 px-4 py-2 text-base font-medium text-white shadow-xs hover:bg-indigo-700 focus:outline-hidden focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 sm:col-start-1 sm:text-sm"
                        )}
                        disabled={claimDialog?.claiming}
                      >
                        {claimDialog?.claiming && <Spinner className="w-3 h-auto text-white mr-2" />}
                        Claim
                      </button>
                      <button
                        type="button"
                        className="mt-3 inline-flex w-full justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-base font-medium text-gray-700 shadow-xs hover:bg-gray-50 focus:outline-hidden focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 sm:col-start-2 sm:mt-0 sm:text-sm"
                        onClick={() => setClaimDialog(null)}
                      >
                        Cancel
                      </button>
                    </div>
                  </form>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition.Root>
    </>
  );
}
